/**
 * Surfaces the FacilityAgreement overlay that priced the current quote.
 *
 * Rendered only when the API returned `appliedFacility !== null` from
 * `POST /quotes/calculate` — i.e. the calling user is a broker on an
 * active facility (PR #4c). Internal Lombard underwriters never see
 * this card because they price the technical book rate.
 *
 * The card is read-only: every value here is server-derived and
 * authoritative. The broker cannot edit the facility from the SPA —
 * facility changes are an Admin Module workflow (PR #5).
 *
 * What we show:
 *   - The facility id + version, so audit lineage is visible.
 *   - The rate-modifier (concession / loading) as a percentage delta.
 *   - The commission band clamp, if the requested brokerage % was
 *     outside the facility band.
 *   - The discretionary range clamp, if the requested adjustment
 *     exceeded the facility's ±range.
 *
 * Each clamp shows BOTH the requested and the clamped value so the
 * broker understands what the API changed and why.
 */

import React from 'react';
import type { AppliedFacility } from '@cortex/domain';

interface AppliedFacilityCardProps {
  readonly facility: AppliedFacility;
}

const formatPct = (value: number): string => `${value.toFixed(2)}%`;

const formatMultiplierDelta = (multiplier: number): string => {
  if (multiplier === 1) return 'No rate adjustment';
  const deltaPct = (multiplier - 1) * 100;
  const sign = deltaPct > 0 ? '+' : '';
  return `${sign}${deltaPct.toFixed(1)}% ${deltaPct > 0 ? 'loading' : 'concession'}`;
};

export const AppliedFacilityCard: React.FC<AppliedFacilityCardProps> = ({ facility }) => {
  const hasMultiplier = facility.netPremiumMultiplier !== 1;
  const hasAnyAdjustment =
    hasMultiplier || facility.commissionClampApplied || facility.discretionaryClampApplied;

  return (
    <section
      aria-label="Facility overlay applied"
      className="bg-white rounded-xl shadow-md border border-slate-200 overflow-hidden font-sans"
    >
      <div className="bg-hr-indigo/95 px-5 py-3 text-white flex items-center justify-between">
        <div className="flex items-center gap-3">
          <span className="text-micro font-black uppercase tracking-widest text-hr-pink">
            Facility
          </span>
          <span className="text-xs font-bold tracking-tight">v{facility.version} applied</span>
        </div>
        <span
          className="text-micro font-mono text-white/60 truncate max-w-[180px]"
          title={facility.facilityId}
        >
          {facility.facilityId.slice(0, 8)}…
        </span>
      </div>

      <div className="p-5 space-y-4">
        <div>
          <p className="text-micro font-bold text-slate-500 uppercase tracking-widest mb-1">
            Pre-agreed rate modifier
          </p>
          <p
            className={`text-sm font-bold ${
              hasMultiplier
                ? facility.netPremiumMultiplier > 1
                  ? 'text-amber-600'
                  : 'text-emerald-600'
                : 'text-slate-700'
            }`}
          >
            {formatMultiplierDelta(facility.netPremiumMultiplier)}
          </p>
        </div>

        {facility.commissionClampApplied && (
          <div className="border-l-4 border-amber-400 bg-amber-50 pl-3 py-2 rounded-r">
            <p className="text-micro font-bold text-amber-700 uppercase tracking-widest mb-1">
              Brokerage % clamped
            </p>
            <p className="text-xs text-amber-900">
              Requested{' '}
              <span className="font-bold font-mono">
                {formatPct(facility.originalBrokeragePct)}
              </span>{' '}
              → applied{' '}
              <span className="font-bold font-mono">{formatPct(facility.clampedBrokeragePct)}</span>
            </p>
          </div>
        )}

        {facility.discretionaryClampApplied && (
          <div className="border-l-4 border-amber-400 bg-amber-50 pl-3 py-2 rounded-r">
            <p className="text-micro font-bold text-amber-700 uppercase tracking-widest mb-1">
              Discretionary adjustment clamped
            </p>
            <p className="text-xs text-amber-900">
              Requested{' '}
              <span className="font-bold font-mono">
                {formatPct(facility.originalDiscretionaryAdjustment)}
              </span>{' '}
              → applied{' '}
              <span className="font-bold font-mono">
                {formatPct(facility.clampedDiscretionaryAdjustment)}
              </span>
            </p>
          </div>
        )}

        {!hasAnyAdjustment && (
          <p className="text-xs text-slate-500 italic">
            Quote priced inside the facility&apos;s pre-agreed band — no overlay adjustments needed.
          </p>
        )}
      </div>
    </section>
  );
};
